import * as Sentry from '@sentry/react'

import { publicCallApi } from '../api'
import { publicCall } from '../utils'

const setLocalPublicCall = info => {
  try {
    window.localStorage.setItem(publicCall.infoKey(), JSON.stringify(info))
  } catch {}
}

const getLocalPublicCall = () => {
  try {
    return JSON.parse(window.localStorage.getItem(publicCall.infoKey())) || {}
  } catch {
    return {}
  }
}

const shouldRedirectToHome = attr => !getLocalPublicCall()[attr || 'name']

const reportError = (where, error) => {
  // eslint-disable-next-line no-console
  console.error(`[appService.${where}]`, error)
  Sentry.captureException(error)
}

const STATUS_LABELS = {
  inscricoes_abertas: 'Inscrições abertas',
  rematricula_aberta: 'Rematrícula aberta',
  resultado_publicado: 'Resultado publicado',
  em_breve: 'Em breve',
  lista_de_espera: 'Lista de espera',
}

const RESULT_WINDOW_DAYS = 60

const deriveStatus = call => {
  const now = Date.now()
  const time = value => (value ? new Date(value).getTime() : NaN)

  if (call.subscribe) return 'inscricoes_abertas'
  if (call.renewEnrollment) return 'rematricula_aberta'

  if (call.published) {
    const publishedAt = time(call.publishAt)
    const windowMs = RESULT_WINDOW_DAYS * 24 * 60 * 60 * 1000

    return !isNaN(publishedAt) && now - publishedAt <= windowMs
      ? 'resultado_publicado'
      : null
  }

  if (time(call.startsAt) > now) return 'em_breve'

  return null
}

const withStatus = call => {
  const status = 'status' in call ? call.status : deriveStatus(call)

  return { ...call, status, statusLabel: STATUS_LABELS[status] || null }
}

// As chamadas entre as quais o candidato escolhe. A mantenedora pode ter mais de
// uma aberta ao mesmo tempo, e é ela quem gerencia o título de cada uma.
const getAvailablePublicCalls = async () => {
  try {
    const response = await publicCallApi.getAvailable()

    if (response.ok && response.status === 200) {
      const responseBody = await response.json()

      return responseBody.data
        .map(item => withStatus({ id: String(item.id), ...item.attributes }))
        .filter(item => !!item.status)
    }
  } catch (error) {
    reportError('getAvailablePublicCalls', error)
  }

  return []
}

const choosePublicCall = id => publicCall.setChosen(id)

const chosenPublicCallId = () => publicCall.chosenId()

const forgetChosenPublicCall = () => publicCall.clearChosen()

const dropStaleChoice = calls => {
  const id = chosenPublicCallId()

  if (!id || !calls.length || calls.some(call => call.id === id)) return false

  forgetChosenPublicCall()

  return true
}

const getPublicCallInfo = async ({ retryOnStale = true } = {}) => {
  try {
    const response = await publicCallApi.getInfo()

    const { ok, status } = response

    if (ok && status === 200) {
      const responseBody = await response.json()

      const info = responseBody.data.attributes

      setLocalPublicCall(info)

      return {
        success: true,
        payload: { info },
      }
    }

    // Escolha guardada que não existe mais nesta mantenedora — chamada removida,
    // ou o navegador guardou a escolha feita em outro portal. Sem isto o portal
    // ficaria quebrado até alguém limpar o navegador.
    if (status === 404 && retryOnStale && chosenPublicCallId()) {
      forgetChosenPublicCall()

      return getPublicCallInfo({ retryOnStale: false })
    }

    return {
      success: false,
      status,
      error: `HTTP ${status}`,
      payload: { info: {} },
    }
  } catch (error) {
    reportError('getPublicCallInfo', error)

    return {
      success: false,
      error: error.message || 'NetworkError',
      payload: { info: {} },
    }
  }
}

const bootstrap = async ({ retryOnStale = true } = {}) => {
  publicCall.purgeInfo()

  let calls = []

  if (!chosenPublicCallId()) {
    calls = await getAvailablePublicCalls()

    if (calls.length === 1) choosePublicCall(calls[0].id)

    if (calls.length > 1) return { calls, needsChoice: true }
  }

  const info = await getPublicCallInfo({ retryOnStale: false })

  if (info.status === 404 && retryOnStale && chosenPublicCallId()) {
    forgetChosenPublicCall()

    return bootstrap({ retryOnStale: false })
  }

  return { calls, needsChoice: false, info }
}

const infoService = {
  bootstrap,
  chosenPublicCallId,
  choosePublicCall,
  dropStaleChoice,
  forgetChosenPublicCall,
  getAvailablePublicCalls,
  getPublicCallInfo,
  getLocalPublicCall,
  shouldRedirectToHome,
}

export default infoService
